import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
} from '@material-ui/core';
import * as VideoExpress from '@vonage/video-express';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import useStyles from './styles';
import usePreviewPublisher from '../../hooks/usePreviewPublisher';
import AudioSettings from '../AudioSetting';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import VideoSettings from '../VideoSetting';
import DeviceAccessAlert from '../DeviceAccessAlert';
import { UserContext } from '../../context/UserContext';
import LinearProgress from '@material-ui/core/LinearProgress';
import { DEVICE_ACCESS_STATUS } from './../constants';
import VideoFilter from '../VideoFilter';
import { useSelector, useDispatch } from 'react-redux';
import {
  SET_BACKGROUND_FILTER,
  SET_LOCAL_AUDIO,
  SET_LOCAL_VIDEO,
} from 'store/WaitingRoom/waitingRoomAction';
import axios from 'axios';
import { checkCanControl } from 'utils';

export default function WaitingRoom({ location }) {
  const dispatch = useDispatch();
  const params = useParams();
  const classes = useStyles();
  const { push } = useHistory();
  const { user, setUser } = useContext(UserContext);
  const waitingRoomVideoContainer = useRef();
  const roomToJoin = location?.state?.room || '';
  const [roomName, setRoomName] = useState(roomToJoin);
  const [userName, setUserName] = useState('');
  const [isRoomNameInvalid, setIsRoomNameInvalid] = useState(false);
  const [isUserNameInvalid, setIsUserNameInvalid] = useState(false);
  const [localVideoSource, setLocalVideoSource] = useState(undefined);
  const [localAudioSource, setLocalAudioSource] = useState(undefined);
  const [localAudioOutput, setLocalAudioOutput] = useState(undefined);
  let [audioDevice, setAudioDevice] = useState('');
  let [videoDevice, setVideoDevice] = useState('');
  const [broadcastType, setBroadcastType] = useState('manual');
  // const [backgroundBlur, setBackgroundBlur] = useState(user.videoEffects.backgroundBlur);
  let [audioOutputDevice, setAudioOutputDevice] = useState('');
  const [videoFilter, setVideoFilter] = useState({
    filterName: '',
    filterPayload: '',
  });
  const {
    createPreview,
    destroyPreview,
    previewPublisher,
    logLevel,
    previewMediaCreated,
    deviceInfo,
    accessAllowed,
  } = usePreviewPublisher();
  const state = useSelector((state) => state.waitingRoomReducer);
  const [data, setData] = useState({});

  // Handle dropdown change
  const handleBroadcastTypeChange = (event) => {
    sessionStorage.setItem('broadcastType', event.target.value);
    setBroadcastType(event.target.value);
  };
  const handleVideoSource = React.useCallback(
    (e) => {
      const videoDeviceId = e.target.value;
      setVideoDevice(e.target.value);
      previewPublisher.setVideoDevice(videoDeviceId);
      setLocalVideoSource(videoDeviceId);
      if (params.meetingFlag === 'broadcast') {
        localStorage.setItem('selectedVideoDevice', videoDeviceId);
      }
    },
    [previewPublisher, setVideoDevice, setLocalVideoSource, params.meetingFlag]
  );

  const handleAudioSource = React.useCallback(
    (e) => {
      const audioDeviceId = e.target.value;
      setAudioDevice(audioDeviceId);
      previewPublisher.setAudioDevice(audioDeviceId);
      setLocalAudioSource(audioDeviceId);
    },
    [previewPublisher, setAudioDevice, setLocalAudioSource]
  );

  const handleAudioOutput = React.useCallback(
    (e) => {
      const audioOutputId = e.target.value;
      setAudioOutputDevice(audioOutputId);
      setLocalAudioOutput(audioOutputId);
    },
    [setLocalAudioOutput, setAudioOutputDevice]
  );

  const redirectHttps = React.useCallback(() => {
    const url = window.location.href;
    if (
      url.toString().indexOf('http://') === 0 &&
      url.toString().indexOf('http://localhost') !== 0
    ) {
      window.location.href = window.location.href
        .toString()
        .replace('http://', 'https://');
    } else {
      return;
    }
  }, []);

  // const addParticipants = async () => {
  //   try {
  //     const response = await axios.post(
  //       `${process.env.REACT_APP_API_URL_NET}/Play/AddParticipantsList?vId=${params.pid}&userId=${params.userName}`
  //     );
  //   } catch (error) {
  //     console.error('Error adding participants:', error);
  //   }
  // };

  const handleJoinClick = () => {
    localStorage.setItem('username', params?.username);
    //addParticipants();
    if (params.meetingFlag === 'broadcast') {
      sessionStorage.setItem('meetingFlag', params.meetingFlag);
      push(`/broadcast/${params.pid}/${params.userId}/${params.roleName}`);
      window.parent.postMessage(JSON.stringify({ action: 'joinClick' }), '*');
    } else {
      push(
        `/WebMeeting/${params.pid}/${params.userName}/${params.userId}/${params.roleName}`
      );
    }
  };

  const validateForm = () => {
    if (userName === '') {
      setIsUserNameInvalid(true);
      return false;
    } else if (roomName === '') {
      setIsRoomNameInvalid(true);
      return false;
    }
    return true;
  };

  const onChangeRoomName = (e) => {
    const roomName = e.target.value;
    if (roomName === '' || roomName.trim() === '') {
      // Space detected
      setRoomName('');
      return;
    }
    setIsRoomNameInvalid(false);
    setRoomName(roomName);
  };

  const onChangeParticipantName = (e) => {
    const userName = e.target.value;
    if (userName === '' || userName.trim() === '') {
      // Space detected
      setUserName('');
      return;
    }
    setIsUserNameInvalid(false);
    setUserName(userName);
  };

  const onKeyDown = (e) => {
    if (e.keyCode === 13) {
      handleJoinClick();
    }
  };

  const handleAudioChange = React.useCallback((e) => {
    dispatch({ type: SET_LOCAL_AUDIO, payload: e.target.checked });
    localStorage.setItem('localAudio', e.target.checked);
  }, []);

  const handleVideoChange = React.useCallback((e) => {
    dispatch({ type: SET_LOCAL_VIDEO, payload: e.target.checked });
    localStorage.setItem('localVideo', e.target.checked);
  }, []);

  const handleChangeVideoFilter = React.useCallback(
    async (filterName, filterPayload) => {
      if (previewPublisher && filterName) {
        switch (filterName) {
          case 'reset':
            await previewPublisher.clearVideoFilter();
            dispatch({
              type: SET_BACKGROUND_FILTER,
              payload: { filterName: '', filterPayload: '' },
            });
            localStorage.setItem('filterName', '');
            localStorage.setItem('filterPayload', '');
            break;

          case 'blur':
            await previewPublisher.setVideoFilter({
              type: 'backgroundBlur',
              blurStrength: filterPayload,
            });
            dispatch({
              type: SET_BACKGROUND_FILTER,
              payload: { filterName, filterPayload },
            });
            localStorage.setItem('filterName', filterName);
            localStorage.setItem('filterPayload', filterPayload);
            break;

          case 'backgroundImage':
            await previewPublisher.setVideoFilter({
              type: 'backgroundReplacement',
              backgroundImgUrl: filterPayload,
            });
            dispatch({
              type: SET_BACKGROUND_FILTER,
              payload: { filterName, filterPayload },
            });
            localStorage.setItem('filterName', filterName);
            localStorage.setItem('filterPayload', filterPayload);
            break;

          default:
          // do nothing
        }
      }
    },
    [previewPublisher]
  );

  // const handleChangeBackgroundBlur = React.useCallback(async () => {
  //   try {
  //     if (backgroundBlur) {
  //       setBackgroundBlur(false);
  //       destroyPreview();
  //       stopEffect();
  //       createPreview(waitingRoomVideoContainer.current, {
  //         videoSource: localVideoSource,
  //       });
  //     } else {
  //       setBackgroundBlur(true);
  //       destroyPreview();
  //       const outputVideoStream = await startBackgroundBlur(videoDevice);
  //       console.log(outputVideoStream);
  //       createPreview(waitingRoomVideoContainer.current, {
  //         videoSource: outputVideoStream.getVideoTracks()[0],
  //         mirror: true,
  //       });
  //     }
  //   } catch (e) {
  //     console.log(`Could not send background blurring - ${e}`);
  //   }
  // }, [
  //   backgroundBlur,
  //   destroyPreview,
  //   stopEffect,
  //   createPreview,
  //   localVideoSource,
  //   videoDevice,
  //   startBackgroundBlur,
  // ]);

  // useEffect(() => {
  //   redirectHttps();
  //   if (localStorage.getItem("username")) {
  //     setUserName(localStorage.getItem("username"));
  //   }
  // }, [redirectHttps]);

  useEffect(() => {
    if (params?.meetingFlag === 'broadcast') {
      const userData = JSON.parse(sessionStorage.getItem('userData'));
      const fetchPermissions = async () => {
        await checkCanControl(params.pid, userData.userId);
      };
      fetchPermissions();
    }
  }, [params]);

  useEffect(() => {
    localStorage.setItem('localAudio', false);
    localStorage.setItem('localVideo', false);
    localStorage.setItem('filterName', '');
    localStorage.setItem('filterPayload', '');
    dispatch({
      type: SET_BACKGROUND_FILTER,
      payload: {
        filterName: '',
        filterPayload: '',
      },
    });
    const userData = sessionStorage.getItem('userData');
    if (userData !== 'undefined') {
      const data = JSON.parse(userData);
      setData(data);
    }
  }, []);

  useEffect(() => {
    localStorage.getItem('localAudio')
      ? dispatch({
        type: SET_LOCAL_AUDIO,
        payload: JSON.parse(localStorage.getItem('localAudio')),
      })
      : localStorage.setItem('localAudio', state.localAudio);
    localStorage.getItem('localVideo')
      ? dispatch({
        type: SET_LOCAL_VIDEO,
        payload: JSON.parse(localStorage.getItem('localVideo')),
      })
      : localStorage.setItem('localVideo', state.localVideo);
    if (!state.localAudio && previewPublisher) {
      previewPublisher.disableAudio();
    }
    if (!state.localVideo && previewPublisher) {
      previewPublisher.disableVideo();
    }
  }, [previewPublisher]);

  useEffect(() => {
    if (
      state.localAudio !== user.defaultSettings.publishAudio ||
      state.localVideo !== user.defaultSettings.publishVideo ||
      localAudioSource !== user.defaultSettings.audioSource ||
      localVideoSource !== user.defaultSettings.videoSource ||
      state.backgroundFilter.filterName !== user.videoFilter.filterName ||
      state.backgroundFilter.filterPayload !== user.videoFilter.filterPayload ||
      localAudioOutput !== user.defaultSettings.audioOutput
    ) {
      setUser({
        ...user,
        videoFilter: {
          filterName: state.backgroundFilter.filterName,
          filterPayload: state.backgroundFilter.filterPayload,
        },
        defaultSettings: {
          publishAudio: state.localAudio,
          publishVideo: state.localVideo,
          audioSource: localAudioSource,
          videoSource: localVideoSource,
          audioOutput: localAudioOutput,
        },
      });
    }
  }, [
    state.localAudio,
    state.localVideo,
    user,
    setUser,
    localAudioSource,
    localVideoSource,
    state.backgroundFilter,
    localAudioOutput,
  ]);

  useEffect(() => {
    if (userName !== user.userName) {
      setUser({ ...user, userName: userName });
    }
  }, [userName, user, setUser]);

  useEffect(() => {
    if (previewPublisher && previewMediaCreated && deviceInfo) {
      previewPublisher.getAudioDevice().then((currentAudioDevice) => {
        setAudioDevice(currentAudioDevice.deviceId);
      });

      if (params.meetingFlag === 'broadcast') {
        const savedVideoDeviceId = localStorage.getItem('selectedVideoDevice');
        const currentVideoDevice = previewPublisher.getVideoDevice();
        if (savedVideoDeviceId && savedVideoDeviceId !== currentVideoDevice.deviceId) {
          previewPublisher.setVideoDevice(savedVideoDeviceId);
          setVideoDevice(savedVideoDeviceId);
          setLocalVideoSource(savedVideoDeviceId);
        } else {
          setVideoDevice(currentVideoDevice.deviceId);
        }
      } else {
        const currentVideoDevice = previewPublisher.getVideoDevice();
        setVideoDevice(currentVideoDevice.deviceId);
      }

      VideoExpress.getActiveAudioOutputDevice().then(
        (currentAudioOutputDevice) => {
          setAudioOutputDevice(currentAudioOutputDevice.deviceId);
        }
      );
    }
  }, [deviceInfo, previewPublisher, previewMediaCreated, params.meetingFlag]);

  useEffect(() => {
    if (previewPublisher) {
      if (state.localAudio && !previewPublisher.isAudioEnabled()) {
        previewPublisher.enableAudio();
      } else if (!state.localAudio && previewPublisher.isAudioEnabled()) {
        previewPublisher.disableAudio();
      }
    }
  }, [state.localAudio, previewPublisher]);

  useEffect(() => {
    if (previewPublisher) {
      if (state.localVideo && !previewPublisher.isVideoEnabled()) {
        previewPublisher.enableVideo();
      } else if (!state.localVideo && previewPublisher.isVideoEnabled()) {
        previewPublisher.disableVideo();
      }
    }
  }, [state.localVideo, previewPublisher]);

  useEffect(() => {
    if (waitingRoomVideoContainer.current) {
      createPreview(waitingRoomVideoContainer.current);
    }

    return () => {
      // stopEffect();
      destroyPreview();
    };
  }, [createPreview, destroyPreview]);

  return (
    <div className={classes.mainWrapper}>
      <div className={classes.waitingRoomContainer}>
        <Grid
          container
          direction='column'
          justifyContent='center'
          alignItems='center'
        >
          <form className={classes.form} noValidate>
            <TextField
              variant='outlined'
              margin='normal'
              id='room-name'
              label='Room Name'
              name='title'
              value={data?.videoTitle}
              focused={data?.videoTitle && true}
            />
            <TextField
              variant='outlined'
              margin='normal'
              id='publisher-name'
              label='Name'
              name='username'
              value={data?.name}
              focused={data?.name && true}
            />
            <div className={classes.mediaSources}>
              {deviceInfo && previewMediaCreated && (
                <>
                  <FormControl>
                    <InputLabel id='audioSource'>
                      Select Audio Source
                    </InputLabel>
                    <Select
                      labelId='audioSource'
                      id='demo-simple-select'
                      value={audioDevice}
                      onChange={handleAudioSource}
                    >
                      {deviceInfo.audioInputDevices.map((device) => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                          {device.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <InputLabel id='audioOutput'>
                      Select Audio Output
                    </InputLabel>
                    {deviceInfo.audioOutputDevices && (
                      <Select
                        labelId='audioOutput'
                        id='demo-simple-select'
                        value={audioOutputDevice}
                        onChange={handleAudioOutput}
                      >
                        {deviceInfo.audioOutputDevices.map((device) => (
                          <MenuItem
                            key={device.deviceId}
                            value={device.deviceId}
                          >
                            {device.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                </>
              )}

              {deviceInfo && previewMediaCreated && (
                <FormControl>
                  <InputLabel id='video'>Select Video Source</InputLabel>
                  {deviceInfo.videoInputDevices && (
                    <Select
                      labelId='video'
                      id='demo-simple-select'
                      value={videoDevice}
                      onChange={handleVideoSource}
                    >
                      {deviceInfo.videoInputDevices.map((device) => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                          {device.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              )}
            </div>
          </form>
          <div className='waiting-tools'>
            <div
              id='waiting-room-video-container'
              className={classes.waitingRoomVideoPreview}
              ref={waitingRoomVideoContainer}
            ></div>
            <div className={classes.deviceContainer}>
              <AudioSettings
                className={classes.deviceSettings}
                hasAudio={state.localAudio}
                onAudioChange={handleAudioChange}
              />
              <LinearProgress
                variant='determinate'
                value={state.localAudio ? logLevel : ''}
              />
              <VideoSettings
                className={classes.deviceSettings}
                hasVideo={state.localVideo}
                onVideoChange={handleVideoChange}
              />
              {params.meetingFlag === 'broadcast' && (
                <FormControl
                  className={classes.formControl}
                  style={{ marginTop: '10px' }}
                >
                  <InputLabel
                    id='broadcast-type-label'
                    style={{ fontWeight: 'bold', fontSize: '1.2rem' }}
                  >
                    Broadcast Type
                  </InputLabel>
                  <Select
                    labelId='broadcast-type-label'
                    id='broadcast-type'
                    value={broadcastType}
                    onChange={handleBroadcastTypeChange}
                    disabled
                  >
                    <MenuItem value='manual'>Manual</MenuItem>
                    <MenuItem value='auto'>Auto</MenuItem>
                  </Select>
                </FormControl>
              )}
            </div>
          </div>

          <VideoFilter handleChangeVideoFilter={handleChangeVideoFilter} />
        </Grid>
        <Grid
          container
          direction='column'
          justifyContent='center'
          alignItems='center'
        >
          <Button variant='contained' color='primary' onClick={handleJoinClick}>
            Join
          </Button>
        </Grid>
      </div>
      {accessAllowed !== DEVICE_ACCESS_STATUS.ACCEPTED && (
        <DeviceAccessAlert accessStatus={accessAllowed}></DeviceAccessAlert>
      )}
    </div>
  );
}
